

















































import gql from 'graphql-tag';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiSelect from '@/components/ui/UiSelect.vue';
import UiSelect2 from '@/components/ui/UiSelect2.vue';
import UiTextEdit from '@/components/ui/UiTextEdit.vue';
import {
  AccrualCadence,
  ArcYieldWalletData,
  isArcYieldWalletDataValid,
  isEvmAddress,
  parentWalletOptions,
  utcMidnightSEC,
} from '@/utils/attachedWallets';
import { stringifyError } from '@/utils/error';

import { baConfig } from '../../../config';
import { ApiSvcWalletResponseDTO, WalletsApi } from '../../../generated/api-svc';

// Arc yield (attached): a DeFi wallet that belongs to a parent wallet, usually a BitGo Connection
// wallet. Picks the parent, the Arc address (prefilled from the parent when it has one), the accrual
// cadence and an optional book-from date. CreateWalletNew builds the rest (see arcYieldWalletFields).
@Component({
  components: {
    UiDatePicker,
    UiSelect,
    UiSelect2,
    UiTextEdit,
  },
  apollo: {
    // A Connection wallet's provider is its connection's; BitGo ones are offered first.
    connections: {
      query: gql`
        query GetConnections($orgId: ID!) {
          connections(orgId: $orgId, overrideCache: true) {
            id
            provider
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
        };
      },
    },
  },
})
export default class ArcYieldWalletInput extends BaseVue {
  public wallets: ApiSvcWalletResponseDTO[] = [];
  public connections: { id: string; provider: string }[] = [];
  public isLoadingWallets = false;
  public parentWalletId = '';
  public walletAddress = '';
  public accrualCadence: AccrualCadence = 'hourly';
  public bookFrom = '';
  // The address last prefilled from a parent: picking another parent refills it, unless the user
  // has typed their own.
  public prefilledAddress = '';

  public readonly cadences = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
  ];

  public get parentOptions() {
    return parentWalletOptions(this.wallets, this.connections);
  }

  public get isWalletAddressValid() {
    return isEvmAddress(this.walletAddress);
  }

  public get inputData(): ArcYieldWalletData {
    return {
      parentWalletId: this.parentWalletId,
      walletAddress: this.walletAddress,
      accrualCadence: this.accrualCadence,
      syncStartDateSEC: utcMidnightSEC(this.bookFrom),
    };
  }

  public get isValid() {
    return isArcYieldWalletDataValid(this.inputData);
  }

  @Watch('inputData', { immediate: true })
  public onDataChange() {
    this.$emit('data', this.inputData);
  }

  @Watch('isValid', { immediate: true })
  public validChanged() {
    this.$emit('valid', this.isValid);
  }

  @Watch('parentWalletId')
  public onParentChange() {
    if (this.walletAddress.trim() === '' || this.walletAddress === this.prefilledAddress) {
      this.walletAddress = this.parentOptions.find((option) => option.id === this.parentWalletId)?.address ?? '';
      this.prefilledAddress = this.walletAddress;
    }
  }

  mounted() {
    this.loadWallets();
  }

  public async loadWallets() {
    this.isLoadingWallets = true;
    try {
      const api = new WalletsApi(undefined, baConfig.api3Url);
      const resp = await api.getWallets(
        this.orgId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false, // loadBalances
        false, // loadBalancesFairValue
        undefined,
        undefined,
        false, // excludeDisabled: it also drops sync-disabled wallets, which can still be parents
        undefined,
        undefined,
        { withCredentials: true }
      );
      this.wallets = resp.data.items;
    } catch (e) {
      this.showErrorSnackbar('Failed to load wallets: ' + stringifyError(e, { hideErrorName: true }));
    } finally {
      this.isLoadingWallets = false;
    }
  }
}
